import React, { useCallback } from 'react';
import type { RegistryComponentProps } from '../../pages/registry/componentRegistry';
import { getRegistryData } from '../../pages/registry/componentRegistry';
import { usePageDataContextOptional } from '../../contexts/PageDataContext';

/** Default lobster projection parameters. Owned by ResetLobsterProjection. */
export const LOBSTER_DEFAULT_PARAMS = {
  herringSupply: [6741, 10885, 15435] as number[],
  menhadenSupply: [60000, 48000, 48000] as number[],
  importUnitValue: 0.18,
  projectionYears: 3,
  fuelPrice: 3.0,
  trapPrice: 180.0,
  ropePrice: 0.5,
  ropelessGear: false,
};

interface LobsterDefaultParams {
  herringSupply: number[];
  menhadenSupply: number[];
  importUnitValue: number;
  projectionYears: number;
  fuelPrice: number;
  trapPrice: number;
  ropePrice: number;
  ropelessGear: boolean;
}

interface ResetLobsterProjectionData {
  /** Override: called when user clicks Reset to Default. If provided, replaces built-in logic. */
  onResetLobster?: () => void;
  /** Disabled when updating/loading. */
  resetLobsterDisabled?: boolean;
}

/**
 * Reset button for lobster projection.
 * Owns the default values; calls onResetLobsterToDefaults with params when page provides it.
 * Otherwise falls back to onResetLobster (legacy) or page resetLobsterProjection.
 */
export default function ResetLobsterProjection(props: RegistryComponentProps): React.ReactElement {
  const pageData = usePageDataContextOptional();
  const data = getRegistryData<ResetLobsterProjectionData>(props);
  const overrideReset = data.onResetLobster;
  const pageResetLobster = pageData?.actions?.resetLobsterProjection as (() => void) | undefined;
  const onResetToDefaults = pageData?.actions?.onResetLobsterToDefaults as ((params: LobsterDefaultParams) => void) | undefined;
  const lobsterLoading = (pageData?.data?.lobsterLoading as boolean | undefined) ?? false;
  const updatingPStar = (pageData?.data?.updatingPStar as boolean | undefined) ?? false;
  const disabled = (data.resetLobsterDisabled ?? updatingPStar ?? lobsterLoading) ?? false;

  const handleReset = useCallback(() => {
    if (typeof overrideReset === 'function') {
      overrideReset();
      return;
    }
    if (typeof onResetToDefaults === 'function') {
      onResetToDefaults(LOBSTER_DEFAULT_PARAMS);
      return;
    }
    if (typeof pageResetLobster === 'function') {
      pageResetLobster();
    }
  }, [overrideReset, onResetToDefaults, pageResetLobster]);

  const handleClick = useCallback(() => {
    handleReset();
  }, [handleReset]);

  return (
    <div style={{ width: '100%', display: 'flex', justifyContent: 'center', padding: '8px 0' }}>
      <button
        className="btn noscroll-refresh-database-button"
        onClick={handleClick}
        disabled={disabled}
      >
        Reset to Default
      </button>
    </div>
  );
}
